import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ImageViewerModal({ images, initialIndex = 0, onClose }) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  if (!images || images.length === 0) return null;

  const currentImage = images[activeIndex];
  const imageUrl = `${API}/journal/images/${currentImage.id || currentImage.filename}`;

  const handleDownload = async () => {
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = currentImage.filename || 'image.jpg';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  const goNext = () => setActiveIndex(i => Math.min(i + 1, images.length - 1));
  const goPrev = () => setActiveIndex(i => Math.max(i - 1, 0));

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 z-[100] flex flex-col items-center justify-center"
        onClick={onClose}
        data-testid="image-viewer-modal"
      >
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 z-10" onClick={e => e.stopPropagation()}>
          <span className="text-white/60 text-sm">
            {images.length > 1 ? `${activeIndex + 1} / ${images.length}` : ''}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              data-testid="image-viewer-download"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              data-testid="image-viewer-close"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main image */}
        <div className="flex-1 flex items-center justify-center w-full px-4 py-16" onClick={e => e.stopPropagation()}>
          {images.length > 1 && activeIndex > 0 && (
            <button
              onClick={goPrev}
              className="absolute left-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
              data-testid="image-viewer-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          <motion.img
            key={activeIndex}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            src={imageUrl}
            alt=""
            className="max-w-full max-h-[75vh] object-contain rounded-lg select-none"
            draggable={false}
            data-testid="image-viewer-main"
          />
          {images.length > 1 && activeIndex < images.length - 1 && (
            <button
              onClick={goNext}
              className="absolute right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
              data-testid="image-viewer-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="pb-6 flex items-center gap-2 px-4 overflow-x-auto" onClick={e => e.stopPropagation()}>
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === activeIndex ? 'border-white scale-110' : 'border-transparent opacity-50 hover:opacity-80'
                }`}
                data-testid={`image-viewer-thumb-${idx}`}
              >
                <img
                  src={`${API}/journal/images/${img.id || img.filename}?variant=thumb`}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export default ImageViewerModal;
